import React, { useRef, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { UploadCloud, X, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function FileUpload({ value = [], onChange, category = "attachment", accept, max = 8, testid = "file-upload" }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const onFiles = async (files) => {
    const arr = Array.from(files);
    if (value.length + arr.length > max) {
      toast.error(`Max ${max} files`);
      return;
    }
    setBusy(true);
    const results = [];
    try {
      for (const f of arr) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("category", category);
        const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        results.push(data);
      }
      onChange([...value, ...results]);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = (id) => onChange(value.filter((f) => f.id !== id));

  return (
    <div className="space-y-3">
      <button
        type="button"
        data-testid={`${testid}-trigger`}
        onClick={() => inputRef.current?.click()}
        disabled={busy}
        className="w-full border hairline border-dashed p-8 flex flex-col items-center justify-center gap-2 hover:border-primary/60 transition-colors duration-300 text-muted-foreground"
      >
        <UploadCloud className="h-6 w-6" strokeWidth={1} />
        <span className="text-xs uppercase tracking-widest">{busy ? "Uploading…" : "Click to upload"}</span>
        <span className="text-xs">JPG · PNG · PDF · STL · STEP · OBJ · ZIP · up to 25MB</span>
      </button>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={accept}
        className="hidden"
        onChange={(e) => e.target.files && onFiles(e.target.files)}
        data-testid={`${testid}-input`}
      />
      {value.length > 0 && (
        <ul className="space-y-2">
          {value.map((f) => (
            <li key={f.id} className="flex items-center justify-between border hairline px-3 py-2">
              <span className="flex items-center gap-3 text-sm truncate">
                <FileText className="h-4 w-4 text-primary" strokeWidth={1} />
                <span className="truncate">{f.name}</span>
                <span className="text-xs text-muted-foreground">{(f.size / 1024).toFixed(0)} KB</span>
              </span>
              <Button variant="ghost" size="sm" onClick={() => remove(f.id)} data-testid={`${testid}-remove-${f.id}`} aria-label={`Remove ${f.name}`}>
                <X className="h-4 w-4" strokeWidth={1} />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
